<template>
  <div ref="root" class="sp-modal" :class="classModifiers">
    <slot name="activator" @click.stop="open = true" />

    <component
      :is="contentComponent"
      v-bind="$props"
      :open="safeOpen"
      :scrollable="false"
      :width="width"
      :is-hidden-temporarily="isHiddenTemporarily"
      :no-content-padding="noMobileContentPadding"
      :expand-to-full-height="expandToFullHeight"
      @close="handleDialogClose"
      @update-open="handleDialogUpdateOpen"
    >
      <sp-card class="sp-modal__card" variant="flat" :subtitle="subtitle" :scrollable-content="scrollableContent" grid>
        <div slot="title" class="sp-modal__card-title">
          <slot name="title">
            <sp-card-title v-if="modalTitle" :oneline="titleOneline">{{ modalTitle }}</sp-card-title>
          </slot>

          <sp-button
            v-if="showCloseButton"
            icon="close-bold"
            :size="closeButtonSize"
            class="close-button"
            color="default"
            @click="open = false"
          >
          </sp-button>
        </div>

        <div slot="content-overlay">
          <slot name="content-overlay" />
        </div>

        <div slot="subtitle">
          <slot name="subtitle" />
        </div>

        <sp-card-content ref="content">
          <div class="sp-modal-alert-container">
            <div class="sp-modal__alert">
              <slot name="alert" />
            </div>
          </div>

          <slot />
        </sp-card-content>

        <sp-card-actions slot="actions">
          <slot name="actions" />
        </sp-card-actions>
      </sp-card>
    </component>
  </div>
</template>

<script setup>
/**
 * The `sp-modal` component is a wrapper around sp-dialog that provides a default layout for the dialog content.
 * It includes a header with a title and a close button, a subtitle, and a slot for the main content.
 * It also provides a slot for the actions that will be displayed at the bottom of the dialog.
 * It also provides a slot for the activator element that will open the dialog when clicked.
 *
 * @slot activator - The element that will open the dialog when clicked.
 * @slot modalTitle - The title of the dialog.
 * @slot subtitle - The subtitle of the dialog.
 * @slot actions - The actions that will be displayed at the bottom of the dialog.
 *
 * @emits close - Emitted when the dialog is closed.
 * @emits update-open - Emitted when the open property is updated.
 */
import { watchImmediate } from "@vueuse/core";
import { computed, onMounted, ref, watch } from "vue";
import { useBreakpoints } from "../../composables/breakpoints";
import { useExpose } from "../../composables/expose.js";
import { toBoolean } from "../../utils/props";
import { props as dialogProps } from "../SpDialog/props.js";

const emit = defineEmits(["close", "update-open"]);

const props = defineProps({
  ...dialogProps,
  modalTitle: {
    type: String,
    default: undefined,
  },
  titleOneline: {
    type: [Boolean, String],
    default: true,
  },
  subtitle: {
    type: String,
    default: undefined,
  },
  closeable: {
    type: [Boolean, String],
    default: undefined,
  },
  scrollableContent: {
    type: [Boolean, String],
    default: true,
  },
  /**
   * Whether the modal should represent a bottom sheet in mobile view.
   * If `true`, the modal will not be displayed as a bottom sheet in mobile view.
   *
   * @type {Boolean}
   * @default false
   */
  noBottomSheet: {
    type: [Boolean, String],
    default: false,
  },
  /**
   * Whether the modal should have no content padding.
   * If `true`, the modal will have no content padding.
   *
   * @type {Boolean}
   * @default false
   */
  noMobileContentPadding: {
    type: [Boolean, String],
    default: false,
  },
  /**
   * Whether the modal should expand to full height.
   * If `true`, the modal will expand to full height.
   *
   * @type {Boolean}
   * @default false
   */
  expandToFullHeight: {
    type: [Boolean, String],
    default: false,
  },
});

const persistent = ref(toBoolean(props.persistent));
watch(
  () => props.persistent,
  (value) => (persistent.value = toBoolean(value)),
);

const closeable = ref(toBoolean(props.closeable));
watch(
  () => props.closeable,
  (value) => (closeable.value = toBoolean(value)),
);

const titleOneline = ref(toBoolean(props.titleOneline));
watch(
  () => props.titleOneline,
  (value) => (titleOneline.value = toBoolean(value)),
);

const showCloseButton = computed(() => closeable.value || (!persistent.value && props.closeable === undefined));

const safeOpen = ref(false);
const open = ref(toBoolean(props.open));
watch(
  () => props.open,
  (value) => (open.value = toBoolean(value)),
);

watch(open, (value) => {
  emit("update-open", value);
});

onMounted(() => {
  // We postpone the opening of the dialog until the component is mounted
  // This is to ensure that the dialog is attached to the DOM before it is opened
  // Otherwise, the dialog may run into an InvalidStateError
  watchImmediate(open, (value) => (safeOpen.value = value));
});

const root = ref(null);
const content = ref(null);

const { exposeMethods } = useExpose(root);

exposeMethods({
  scrollContentIntoView,
});
/**
 * Scrolls the content into view.
 * The behavior of this function can be customized by passing an options object.
 * For more information, see the MDN documentation: https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollIntoView
 * @param options {ScrollIntoViewOptions}
 */
function scrollContentIntoView(options) {
  content.value.scrollIntoView(options);
}

const { isGtSmScreen } = useBreakpoints(root);

const noBottomSheet = computed(() => toBoolean(props.noBottomSheet));
const showBottomSheet = computed(() => !noBottomSheet.value && !isGtSmScreen.value);
const contentComponent = computed(() => (showBottomSheet.value ? "sp-bottom-sheet" : "sp-dialog"));
const classModifiers = computed(() => ({
  "--bp-gt-sm": isGtSmScreen.value,
}));

function handleDialogClose() {
  emit("close");
  open.value = false;
}

function handleDialogUpdateOpen({ detail }) {
  const [value] = detail;
  open.value = value;
}

const closeButtonSize = ref("small");

function getCloseButtonSizePropertyValue() {
  return window.getComputedStyle(document.documentElement).getPropertyValue("--sp-comp-modal-header-close-button-size");
}

function setCloseButtonSize() {
  const value = getCloseButtonSizePropertyValue();

  if (value && value !== closeButtonSize.value) {
    closeButtonSize.value = value;
  }
}

onMounted(setCloseButtonSize);
</script>

<style>
:host {
  display: block;
  --modal-header-background-color: var(--sp-comp-modal-header-background-color, var(--sp-sys-color-primary));
  --modal-header-color: var(--sp-comp-modal-header-color, var(--sp-sys-color-on-primary));
  --modal-padding: var(--sp-comp-modal-padding, calc(var(--sp-ref-spacing-8) * 1.5));
  --modal-card-padding: var(--modal-padding);
}
</style>

<style scoped lang="scss">
.sp-modal {
  --card-height: 100%;
  --card-title-min-height: var(--sp-comp-modal-header-min-height, calc(var(--sp-ref-spacing-8, 1rem) * 4));
}
.sp-modal__card {
  --sp-comp-card-height: var(--card-height);
  --sp-card-root-height: var(--card-height);
  --sp-comp-card-padding-inline: var(--modal-card-padding);
}

.sp-modal__card-title {
  box-sizing: border-box;
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: start;
  background-color: var(--modal-header-background-color);
  color: var(--modal-header-color);
  padding: var(--sp-comp-modal-header-padding-block, var(--sp-ref-spacing-4, 0.5rem))
    var(--sp-comp-modal-header-padding-inline, var(--modal-padding));
  font-size: var(--sp-comp-modal-header-font-size, 1.125rem);
  font-weight: var(--sp-comp-modal-header-font-weight, var(--sp-sys-font-weight-bold));
  font-family: var(--sp-comp-modal-header-font-family, var(--sp-sys-font-family-bold));
  border-bottom: var(--sp-comp-modal-header-border-bottom, none);
  min-height: var(--card-title-min-height);
}

.close-button {
  --sp-comp-button-bg-color: var(--sp-comp-modal-close-button-bg-color, var(--modal-header-background-color));
  transform: translateX(var(--sp-comp-modal-close-button-icon-offset, var(--sp-ref-spacing-4, 0.5rem)));
}

.close-button::part(icon) {
  --fill: var(--sp-comp-modal-close-button-icon-fill, var(--modal-header-color));
}

.close-button:focus {
  --sp-comp-button-focus-opacity: 0;
}
.sp-modal-alert-container {
  position: sticky;
  top: var(--sp-comp-modal-alert-container-top, var(--modal-padding));

  width: 100%;
  z-index: var(--sp-comp-modal-alert-z-index, var(--sp-sys-z-index-max, 99));

  .sp-modal__alert {
    position: absolute;
    width: 100%;
  }
}
</style>
